import React, { useState, useEffect } from 'react';
import { 
  FiSettings, FiSave, FiMessageCircle, FiFacebook, FiVideo, 
  FiMail, FiInfo 
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function SettingsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState({
    line_channel_token: '',
    line_channel_secret: '',
    fb_page_token: '',
    fb_verify_token: '',
    tiktok_access_token: '',
    tiktok_app_secret: '',
    emailjs_service_id: '',
    emailjs_template_id: '',
    emailjs_public_key: '',
    emailjs_private_key: ''
  });

  // 🚀 โหลดข้อมูลการตั้งค่าเดิมจาก Database (MySQL ผ่าน api.php)
  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}?action=get_website_settings`);
      const data = await res.json();
      if (data.success && data.data) {
        setSettings({
          line_channel_token: data.data.line_channel_token || '',
          line_channel_secret: data.data.line_channel_secret || '',
          fb_page_token: data.data.fb_page_token || '',
          fb_verify_token: data.data.fb_verify_token || '',
          tiktok_access_token: data.data.tiktok_access_token || '',
          tiktok_app_secret: data.data.tiktok_app_secret || '',
          emailjs_service_id: data.data.emailjs_service_id || '',
          emailjs_template_id: data.data.emailjs_template_id || '',
          emailjs_public_key: data.data.emailjs_public_key || '',
          emailjs_private_key: data.data.emailjs_private_key || ''
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อข้อมูลการตั้งค่าจากฐานข้อมูลได้', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    Swal.fire({ title: 'กำลังบันทึกตั้งค่า...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    
    try {
      const res = await fetch(`${API_URL}?action=update_website_settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      const result = await res.json();
      if (result.success) {
        Swal.fire('บันทึกสำเร็จ!', 'อัปเดตระบบการตั้งค่า API ต่างๆ เรียบร้อยแล้ว', 'success');
        fetchSettings();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      Swal.fire('ผิดพลาด', error.message || 'ไม่สามารถบันทึกการตั้งค่าได้', 'error');
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-10 animate-fade-in text-left">
      
      {/* Header ของหน้าตั้งค่า */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-blue-600 text-white p-3 rounded-xl shadow-lg">
          <FiSettings size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-black text-gray-800 dark:text-white">ตั้งค่าระบบ (System Settings)</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">เชื่อมต่อ API ของแพลตฟอร์มและระบบอีเมลแจ้งเตือนช่างเพื่อเปิดใช้งาน Omnichannel</p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-20 font-bold text-blue-500">กำลังดึงข้อมูลตั้งค่าล่าสุด...</div>
      ) : (
        <form onSubmit={handleSaveSettings} className="space-y-6">
          
          {/* 📬 EmailJS API Integration */}
          <div className="bg-white dark:bg-[#1e293b] p-6 rounded-2xl shadow-sm border border-indigo-200/50 dark:border-indigo-900/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-2 h-full bg-indigo-650"></div>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <span className="text-indigo-600"><FiMail size={20} /></span> ระบบแจ้งเตือนทางอีเมลสำรอง (EmailJS Integration)
              </h2>
              <span className="text-[10px] font-black text-indigo-700 bg-indigo-50 dark:bg-indigo-950/40 px-2.5 py-1 rounded-full flex items-center gap-1">
                <FiInfo /> ใช้เมื่อแอปช่างไม่สั่น/ไม่แจ้งเตือน push
              </span>
            </div>
            
            <p className="text-[10px] text-gray-450 leading-relaxed mb-4">
              กำหนด API Keys ของบริการ EmailJS ในการส่งอีเมลตรงหาช่างทันทีเมื่อหลังบ้านจ่ายงาน (ข้อมูลช่างต้องกรอกช่องอีเมลพนักงานไว้ด้วย)
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1.5">EmailJS Service ID</label>
                <input 
                  type="text" 
                  name="emailjs_service_id" 
                  value={settings.emailjs_service_id} 
                  onChange={handleChange}
                  placeholder="เช่น service_xxxx"
                  className="w-full bg-gray-50 dark:bg-[#0f172a] border border-gray-250 dark:border-[#334155] text-sm text-gray-800 dark:text-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1.5">EmailJS Template ID</label>
                <input 
                  type="text" 
                  name="emailjs_template_id" 
                  value={settings.emailjs_template_id} 
                  onChange={handleChange}
                  placeholder="เช่น template_xxxx"
                  className="w-full bg-gray-50 dark:bg-[#0f172a] border border-gray-250 dark:border-[#334155] text-sm text-gray-800 dark:text-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1.5">EmailJS Public Key</label>
                <input 
                  type="text" 
                  name="emailjs_public_key" 
                  value={settings.emailjs_public_key} 
                  onChange={handleChange}
                  placeholder="เช่น user_xxxx หรือ key_xxxx"
                  className="w-full bg-gray-50 dark:bg-[#0f172a] border border-gray-250 dark:border-[#334155] text-sm text-gray-800 dark:text-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1.5">EmailJS Private Key (Access Token)</label>
                <input 
                  type="password" 
                  name="emailjs_private_key" 
                  value={settings.emailjs_private_key} 
                  onChange={handleChange}
                  placeholder="กรอก Access Token ลับ"
                  className="w-full bg-gray-50 dark:bg-[#0f172a] border border-gray-250 dark:border-[#334155] text-sm text-gray-800 dark:text-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* 🟢 LINE Official Account API */}
          <div className="bg-white dark:bg-[#1e293b] p-6 rounded-2xl shadow-sm border border-[#00B900]/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-2 h-full bg-[#00B900]"></div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-4">
              <span className="text-[#00B900]"><FiMessageCircle size={20} /></span> LINE Messaging API
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1.5">Channel Access Token</label>
                <input 
                  type="text" name="line_channel_token" value={settings.line_channel_token} onChange={handleChange}
                  placeholder="กรอก Channel Access Token (Long-lived)"
                  className="w-full bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-sm text-gray-800 dark:text-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-[#00B900] outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1.5">Channel Secret</label>
                <input 
                  type="password" name="line_channel_secret" value={settings.line_channel_secret} onChange={handleChange}
                  placeholder="กรอก Channel Secret"
                  className="w-full bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-sm text-gray-800 dark:text-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-[#00B900] outline-none"
                />
              </div>
            </div>
          </div>

          {/* 🔵 Facebook Graph API */}
          <div className="bg-white dark:bg-[#1e293b] p-6 rounded-2xl shadow-sm border border-[#1877F2]/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-2 h-full bg-[#1877F2]"></div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-4">
              <span className="text-[#1877F2]"><FiFacebook size={20} /></span> Facebook Messenger API
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1.5">Page Access Token</label>
                <input 
                  type="text" name="fb_page_token" value={settings.fb_page_token} onChange={handleChange}
                  placeholder="กรอก Page Access Token"
                  className="w-full bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-sm text-gray-800 dark:text-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-[#1877F2] outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1.5">Webhook Verify Token</label>
                <input 
                  type="text" name="fb_verify_token" value={settings.fb_verify_token} onChange={handleChange}
                  placeholder="ตั้งค่ารหัสยืนยัน Webhook ด้วยตัวเอง"
                  className="w-full bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-sm text-gray-800 dark:text-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-[#1877F2] outline-none"
                />
              </div>
            </div>
          </div>

          {/* ⚫ TikTok API */}
          <div className="bg-white dark:bg-[#1e293b] p-6 rounded-2xl shadow-sm border border-gray-800/30 dark:border-gray-500/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-2 h-full bg-gray-800 dark:bg-gray-400"></div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-4">
              <span className="text-gray-800 dark:text-gray-400"><FiVideo size={20} /></span> TikTok API
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1.5">Access Token</label>
                <input 
                  type="text" name="tiktok_access_token" value={settings.tiktok_access_token} onChange={handleChange}
                  placeholder="กรอก Access Token"
                  className="w-full bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-sm text-gray-800 dark:text-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-gray-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1.5">App Secret</label>
                <input 
                  type="password" name="tiktok_app_secret" value={settings.tiktok_app_secret} onChange={handleChange}
                  placeholder="กรอก App Secret"
                  className="w-full bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-[#334155] text-sm text-gray-800 dark:text-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-gray-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* ปุ่มบันทึก */}
          <div className="flex justify-end pt-4">
            <button 
              type="submit" 
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-8 rounded-xl flex items-center gap-2 shadow-lg transition-colors border-none cursor-pointer"
            >
              <FiSave size={18} /> บันทึกการตั้งค่า API และระบบเตือนอีเมล
            </button>
          </div>

        </form>
      )}
    </div>
  );
}